import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ArrowDownToLine, ArrowUpFromLine, ChevronLeft, ChevronRight, Droplets, Gift, GlassWater, MessageSquareText, Minus, Plus, Search, Trash2, Truck, Warehouse, X } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { rowsOf } from '@/data/items';
import { addMove, FROM_LABEL, LOSS_LABEL, moveLabel, voidMove } from '@/data/nights';
import { addDays, formatBusinessDate, formatInstant, isBusinessDate } from '@/domain/businessDate';
import { bottleOz as sizeOz } from '@/domain/bottles';
import { isManagerOrAbove } from '@/domain/permissions';
import type { CameFrom, Item, LossReason, Movement, MoveKind } from '@/domain/types';
import { Button, Card, CardTitle, cn, EmptyState, Notice, Spinner } from '@/ui/core';
import { NumberInput, TextInput } from '@/ui/form';
import { useConfirm, useToast } from '@/ui/overlay';
import { ButtonLink } from '@/ui/ButtonLink';
import { useSession } from '@/app/providers';
import { useBusinessToday } from '@/app/hooks';
import { useNightData } from './useNight';

/**
 * What came in or went out on a night: the "Beer Added", "Transfered Out", "Promo/Spillage"
 * and "Liquor Added" rows (the app adds each night's entries together).
 *  promo    staff, from the home screen: promo, spilled, broken, something else
 *  brought  staff, from the home screen: brought up from downstairs (or straight from a delivery)
 *  manager  /admin/moves: everything, for any night
 */
export type MovesMode = 'promo' | 'brought' | 'manager';

const KINDS: { kind: MoveKind; title: string; sub: string; icon: typeof ArrowDownToLine }[] = [
  { kind: 'added', title: 'Came in', sub: 'Brought from downstairs, or a delivery', icon: ArrowDownToLine },
  { kind: 'transfer_out', title: 'Moved out', sub: 'Beer sent to another bar or event', icon: ArrowUpFromLine },
  { kind: 'promo_spill', title: 'Promo, spilled or broken', sub: 'Given away, dropped, or poured out', icon: GlassWater },
];

const REASONS: { reason: LossReason; sub: string; icon: typeof Gift }[] = [
  { reason: 'promo', sub: 'Comped, a promo, a birthday shot', icon: Gift },
  { reason: 'spilled', sub: 'Poured out, knocked over, a bad pour', icon: Droplets },
  { reason: 'broken', sub: 'A dropped or broken bottle or can', icon: GlassWater },
  { reason: 'other', sub: 'Say what in the note', icon: MessageSquareText },
];

const FROMS: { from: CameFrom; sub: string; icon: typeof Gift }[] = [
  { from: 'downstairs', sub: 'From the basement up to the bar or the back', icon: Warehouse },
  { from: 'delivery', sub: 'Went straight to the bar, not the basement', icon: Truck },
];

const BOTTLE_SIZES = [26, 40, 60];

const TITLE: Record<MovesMode, string> = { promo: 'Promo or spillage', brought: 'Brought from downstairs', manager: 'Deliveries, transfers & promo' };
const HELP: Record<MovesMode, string> = { promo: 'promo-spill', brought: 'brought-up', manager: 'deliveries' };

export function MovesPage({ mode = 'manager' }: { mode?: MovesMode }) {
  const params = useParams();
  const [search] = useSearchParams();
  const today = useBusinessToday();
  const navigate = useNavigate();
  const date = params.date ?? today;
  const data = useNightData(date);
  const { actor, people, settings } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const startKind: MoveKind | null = mode === 'promo' ? 'promo_spill' : mode === 'brought' ? 'added' : ((search.get('kind') as MoveKind | null) ?? null);
  const [kind, setKind] = useState<MoveKind | null>(startKind);
  const [reason, setReason] = useState<LossReason | null>(null);
  const [from, setFrom] = useState<CameFrom>('downstairs');
  const [item, setItem] = useState<Item | null>(null);
  const [q, setQ] = useState('');
  const [qty, setQty] = useState<number | null>(null);
  const [bottles, setBottles] = useState<number>(1);
  const [bottleOz, setBottleOz] = useState<number>(40);
  const [note, setNote] = useState('');
  const [broughtBy, setBroughtBy] = useState(actor.userId);
  // A different person took over the tablet: start from them, not whoever was on before.
  useEffect(() => setBroughtBy(actor.userId), [actor.userId]);
  const [busy, setBusy] = useState(false);
  const askedItem = search.get('item');
  const [askedUsed, setAskedUsed] = useState(false);

  const choices = useMemo(() => {
    if (!data) return { beer: [] as Item[], liquor: [] as Item[] };
    const seen = new Set<string>();
    const ordered = [...rowsOf(data.items, 'back_beer'), ...rowsOf(data.items, 'front_beer'), ...rowsOf(data.items, 'liquor'), ...rowsOf(data.items, 'liqueur')].filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const match = (i: Item) => words.every((w) => i.name.toLowerCase().includes(w));
    return { beer: ordered.filter((i) => i.kind === 'beer' && match(i)), liquor: kind === 'transfer_out' ? [] : ordered.filter((i) => i.kind === 'liquor' && match(i)) };
  }, [data, q, kind]);

  if (!isBusinessDate(date)) return <EmptyState title="That page doesn’t exist" action={<ButtonLink to="/">Home</ButtonLink>} />;
  if (!data) return <Spinner />;

  // From a night's check ("add what came in"): start with that drink picked.
  if (askedItem && !askedUsed) {
    const it = data.itemById.get(askedItem);
    setAskedUsed(true);
    if (it) pick(it);
  }

  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? 'Someone';
  const status = data.night?.status ?? 'counting';
  const manager = isManagerOrAbove(actor.role);
  const closed = status === 'approved' || (status === 'finished' && !manager);
  const shown = mode === 'promo' ? data.moves.filter((m) => m.kind === 'promo_spill') : mode === 'brought' ? data.moves.filter((m) => m.kind === 'added') : data.moves;
  const ready = !!kind && (kind !== 'promo_spill' || !!reason);
  const needsNote = reason === 'other' && !note.trim();
  const byBottle = kind === 'added' && item?.kind === 'liquor';
  const amount = byBottle ? bottles * bottleOz : qty;

  function pick(it: Item) {
    setItem(it);
    setQty(null);
    setBottles(1);
    setBottleOz(sizeOz(it.bottleMl) ?? 40);
  }
  const reset = () => {
    setKind(startKind);
    setReason(null);
    setFrom('downstairs');
    setItem(null);
    setQ('');
    setQty(null);
    setBottles(1);
    setNote('');
    setBroughtBy(actor.userId);
  };
  const save = async () => {
    if (!kind || !item || amount == null) return;
    setBusy(true);
    try {
      const m = await addMove({
        date,
        kind,
        itemId: item.id,
        qty: amount,
        note,
        ...(kind === 'promo_spill' && reason ? { reason } : {}),
        ...(kind === 'added' ? { from, broughtBy } : {}),
        ...(byBottle ? { bottles, bottleOz } : {}),
      });
      toast.show(`Saved: ${moveLabel(m).toLowerCase()} — ${item.name}, ${amountText(m)}`);
      reset();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const bigChoice = (selected: boolean, onClick: () => void, Icon: typeof Gift, title: string, sub: string, key: string) => (
    <button key={key} type="button" aria-pressed={selected} onClick={onClick} className={cn('flex min-h-20 items-center gap-3 rounded-2xl border-2 p-3 text-left', selected ? 'border-accent bg-accent-soft' : 'border-border bg-surface hover:border-accent')}>
      <Icon className="size-8 shrink-0 text-accent" aria-hidden />
      <span>
        <span className="block text-lg font-bold leading-tight">{title}</span>
        <span className="block text-sm text-muted">{sub}</span>
      </span>
    </button>
  );
  // Staff pages: the first step is the only choice on the page; the manager's has the kind first.
  const firstStep = mode === 'manager' ? 2 : 1;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4">
      <div>
        <p className="text-base text-muted md:text-lg">{formatBusinessDate(date, settings.locale, 'long')}</p>
        <h1 className="flex items-center gap-1 text-3xl font-bold md:text-4xl">
          {TITLE[mode]} <HelpBubble topic={HELP[mode]} />
        </h1>
        {mode === 'manager' && (
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" icon={<ChevronLeft className="size-4" aria-hidden />} onClick={() => navigate(`/admin/moves/${addDays(date, -1)}`)}>
              Night before
            </Button>
            {date < today && (
              <Button size="sm" icon={<ChevronRight className="size-4" aria-hidden />} onClick={() => navigate(`/admin/moves/${addDays(date, 1)}`)}>
                Next night
              </Button>
            )}
            <label className="flex items-center gap-2 text-sm font-semibold">
              Or pick a night
              <TextInput type="date" value={date} max={today} onChange={(e) => e.target.value && isBusinessDate(e.target.value) && navigate(`/admin/moves/${e.target.value}`)} className="w-44" />
            </label>
          </div>
        )}
      </div>
      {closed && <Notice tone="info">{status === 'approved' ? 'This night is approved. A manager has to reopen it to add anything.' : 'Tonight’s count is finished. Ask a manager to add this.'}</Notice>}

      {!closed && (
        <Card className="flex flex-col gap-4">
          {mode === 'manager' && (
            <div>
              <h2 className="mb-2 text-xl font-bold">1. What happened?</h2>
              <div className="grid gap-2 md:grid-cols-3">
                {KINDS.map((k) =>
                  bigChoice(
                    kind === k.kind,
                    () => {
                      setKind(k.kind);
                      if (k.kind !== 'promo_spill') setReason(null);
                      if (k.kind === 'transfer_out' && item?.kind === 'liquor') setItem(null);
                    },
                    k.icon,
                    k.title,
                    k.sub,
                    k.kind,
                  ),
                )}
              </div>
            </div>
          )}

          {kind === 'promo_spill' && (
            <div>
              <h2 className="mb-2 text-xl font-bold">{mode === 'manager' ? 'Which one?' : '1. What happened?'}</h2>
              <div className="grid gap-2 sm:grid-cols-2">{REASONS.map((r) => bigChoice(reason === r.reason, () => setReason(r.reason), r.icon, LOSS_LABEL[r.reason], r.sub, r.reason))}</div>
            </div>
          )}

          {kind === 'added' && (
            <div>
              <h2 className="mb-2 text-xl font-bold">{mode === 'manager' ? 'Where from?' : '1. Where from?'}</h2>
              <div className="grid gap-2 sm:grid-cols-2">{FROMS.map((f) => bigChoice(from === f.from, () => setFrom(f.from), f.icon, FROM_LABEL[f.from], f.sub, f.from))}</div>
            </div>
          )}

          {ready && (
            <div>
              <h2 className="mb-2 text-xl font-bold">{firstStep + 1}. Which drink?</h2>
              {item ? (
                <div className="flex items-center gap-3 rounded-2xl border-2 border-accent bg-accent-soft p-3">
                  <span className="flex-1 text-xl font-bold">{item.name}</span>
                  <Button onClick={() => setItem(null)} icon={<X className="size-5" aria-hidden />}>
                    Pick another
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted" aria-hidden />
                    <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type part of the name" aria-label="Find a drink" className="min-h-13 pl-10 text-lg" autoFocus={mode === 'brought'} />
                  </div>
                  <div className="flex max-h-[26rem] flex-col gap-3 overflow-y-auto pr-1">
                    {(mode === 'brought' ? (['liquor', 'beer'] as const) : (['beer', 'liquor'] as const)).map((k) =>
                      choices[k].length ? (
                        <div key={k}>
                          <p className="mb-1 text-sm font-bold uppercase tracking-wider text-muted">{k === 'beer' ? 'Beer' : 'Liquor'}</p>
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {choices[k].map((i) => (
                              <button key={i.id} type="button" onClick={() => pick(i)} className="min-h-14 rounded-xl border-2 border-border bg-surface px-3 text-left text-base font-semibold hover:border-accent">
                                {i.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null,
                    )}
                    {choices.beer.length + choices.liquor.length === 0 && <p className="text-muted">Nothing matches “{q}”.</p>}
                  </div>
                </>
              )}
            </div>
          )}

          {ready && item && (
            <div>
              <h2 className="mb-2 text-xl font-bold">{firstStep + 2}. How many?</h2>
              {byBottle ? (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" disabled={bottles <= 1} onClick={() => setBottles(Math.max(1, bottles - 1))} aria-label="One bottle less" className="flex size-14 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40">
                      <Minus className="size-7" aria-hidden />
                    </button>
                    <NumberInput aria-label="Bottles" value={bottles} onValueChange={(v) => setBottles(v != null && v > 0 ? v : 1)} size="xl" align="center" className="w-24" />
                    <button type="button" onClick={() => setBottles(bottles + 1)} aria-label="One bottle more" className="flex size-14 items-center justify-center rounded-xl border border-border bg-surface-2">
                      <Plus className="size-7" aria-hidden />
                    </button>
                    <span className="text-lg font-semibold text-muted">{bottles === 1 ? 'bottle' : 'bottles'} of</span>
                    {[...new Set([...BOTTLE_SIZES, bottleOz])].sort((a, b) => a - b).map((oz) => (
                      <Button key={oz} size="lg" variant={bottleOz === oz ? 'primary' : 'secondary'} aria-pressed={bottleOz === oz} onClick={() => setBottleOz(oz)}>
                        {oz} oz
                      </Button>
                    ))}
                  </div>
                  <p className="text-base text-muted">
                    = <strong className="text-text">{bottles * bottleOz} oz</strong> added to Liquor Added on the sheet.
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <NumberInput aria-label={item.kind === 'beer' ? 'Bottles or cans' : 'Ounces'} value={qty} onValueChange={setQty} decimals={item.kind === 'liquor'} size="xl" align="center" placeholder="—" className="w-36" />
                  <span className="text-lg font-semibold text-muted">{item.kind === 'beer' ? 'bottles or cans' : 'oz'}</span>
                  {item.kind === 'beer' && kind !== 'promo_spill'
                    ? (item.caseSize ? [item.caseSize] : [12, 15, 24]).map((n) => (
                        <Button key={n} size="lg" onClick={() => setQty((qty ?? 0) + n)}>
                          + box of {n}
                        </Button>
                      ))
                    : null}
                  {qty != null && (
                    <Button size="lg" variant="ghost" onClick={() => setQty(null)}>
                      Clear
                    </Button>
                  )}
                </div>
              )}
              {kind === 'added' && (
                <>
                  <label htmlFor="move-who" className="mt-4 block text-sm font-semibold">
                    {from === 'delivery' ? 'Who brought it in?' : 'Who brought it up?'}
                  </label>
                  <select
                    id="move-who"
                    value={broughtBy}
                    onChange={(e) => setBroughtBy(e.target.value)}
                    className="mt-1 min-h-13 w-full max-w-sm rounded-xl border border-border bg-surface-2 px-3 text-lg font-semibold text-text"
                  >
                    {people
                      .filter((p) => p.active || p.id === actor.userId)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.id === actor.userId ? ' (me)' : ''}
                        </option>
                      ))}
                  </select>
                </>
              )}
              <label htmlFor="move-note" className="mt-4 block text-sm font-semibold">
                {reason === 'other' ? 'What happened? (needed)' : 'Note (if you want)'}
              </label>
              <TextInput
                id="move-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={reason === 'promo' ? 'e.g. birthday shot, table 12' : reason === 'spilled' ? 'e.g. knocked over at the bar' : reason === 'broken' ? 'e.g. dropped a case in the back' : reason === 'other' ? 'e.g. bottle was off, poured out' : kind === 'transfer_out' ? 'e.g. sent to the patio bar' : from === 'delivery' ? 'e.g. Tuesday delivery' : ''}
                maxLength={200}
                className="mt-1"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="xl" variant="primary" busy={busy} disabled={amount == null || amount <= 0 || needsNote} onClick={save}>
                  Save
                </Button>
                <Button size="xl" onClick={reset}>
                  Start over
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card>
        <CardTitle>{mode === 'promo' ? 'Promo and spills tonight' : mode === 'brought' ? 'Brought up tonight' : 'This night so far'}</CardTitle>
        {shown.length === 0 ? (
          <p className="text-base text-muted">Nothing yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {shown.map((m) => {
              const it = m.itemId ? data.itemById.get(m.itemId) : null;
              const mine = m.by === actor.userId;
              return (
                <li key={m.id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-semibold">
                      {moveLabel(m)}: {it?.name ?? (m.sheet === 'beer' ? 'Beer (night total)' : 'Liquor (night total)')} — {amountText(m)}
                    </span>
                    <span className="block text-sm text-muted">
                      {m.kind === 'added' ? `${m.from === 'delivery' ? 'Brought in' : 'Brought up'} by ${nameOf(m.broughtBy ?? m.by)}${m.broughtBy ? ` · typed in by ${nameOf(m.by)}` : ''}` : nameOf(m.by)} · {formatInstant(m.at, settings.timezone, settings.locale)}
                      {m.note && <> · {m.note}</>}
                    </span>
                  </span>
                  {!closed && (mine || manager) && (
                    <Button
                      size="sm"
                      icon={<Trash2 className="size-4" aria-hidden />}
                      onClick={async () => {
                        const ok = await confirm({ title: 'Take this off?', body: `${moveLabel(m)}: ${it?.name ?? 'night total'} — ${amountText(m)}. It stays in the history of changes.`, confirmLabel: 'Take it off', danger: true });
                        if (!ok) return;
                        try {
                          await voidMove(m.id);
                        } catch (err) {
                          toast.error(err);
                        }
                      }}
                    >
                      Take off
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      {mode === 'manager' ? (
        <ButtonLink to={`/admin/night/${date}`} size="lg" block>
          Back to this night’s check
        </ButtonLink>
      ) : (
        <ButtonLink to="/" size="xl" block>
          Back to the home screen
        </ButtonLink>
      )}
    </div>
  );
}

/** "48", "12.5 oz", or "2 × 40 oz (80 oz)". */
function amountText(m: Movement): string {
  const n = m.qty.toLocaleString('en-CA');
  if (m.sheet !== 'liquor') return n;
  if (m.bottles != null && m.bottleOz != null) return `${m.bottles} × ${m.bottleOz} oz (${n} oz)`;
  return `${n} oz`;
}
